import {
    ESubscribeMode,
    IBillingInfo,
    ICloudProducts,
    ICommonRes,
    ICountryItem,
    IDeviceModelGroup,
    IGetCloudProductsArgs,
    IGetCloudProductsResponse,
    IGetPlanByIdRes,
    IGetUserAddresses_QueryArgs as IGetUserAddressesQueryArgs,
    IManualRefundInfo,
    IOrder,
    IPlan,
    IPreviewFreeOffer_BodyArgs,
    IProductPlan,
    IProductSeries,
    IReolinkAddresses,
    IStorageLoc,
    ISupportCountry,
    IUserAddresses,
    TCreateUserAddressArg,
    TUpdateUserAddressArg
} from '@reolink-cloud/interface';
import { countriesManager, productsManager } from '@/lib';
import { httpManager as http, stmHttpClient as stmHttp } from '@/plugins/HttpManager';
import { Object as ObjectMethods, DateTime } from '@reolink-web/utils';
import { config } from 'CONFIG';
import dayjs from 'dayjs';
import nprogress from 'nprogress';
import { unfoldError } from '../common/js/notice';
import { isEmptyObject, objectToQueryString } from '../common/js/utils';
import { listToString } from './s3Config';
import { createFreeOrder } from './order';
import { getIANATimeZone } from '@/utils/time';
import type VueRouter from 'vue-router';
import { getQuerySource } from '@/utils/business';

export const getPlan = ( id: string | number ): Promise<any> => {

    return new Promise(( resolve, reject ) => {

        http.get( `/shop/plans/${id}/?client_id=${encodeURIComponent( config.clientId )}` ).then( res => {

            resolve( res.data );

        }).catch( err => {

            console.error( err );
            reject( err );

        });

    });

};

export const getOrderDetailById = ( id: string | number, token?: string ): Promise<IOrder> => {

    return new Promise(( resolve, reject ) => {

        http.get( `/shop/orders/${id}${token ? `?invoice_token=${token}` : ''}` ).then( res => {

            resolve( res.data );

        }).catch( err => {

            console.error( err );
            unfoldError( 'errors.server_error', 10000 );
            reject( err );

        });

    });

};

export const previewSubscriptions = async ( planData: Record<string, any>, shippingAddress?: IShippingAddress ): Promise<any> => {

    const data: any[] = [];
    data.push( planData );
    return new Promise(( resolve, reject ) => {

        http.post( '/shop/orders/preview/', {

            items: data,
            shippingInfo: shippingAddress,
            context: { lang: planData.context.lang }

        }, {

            frequency: 'previewSubscription'

        }).then(({ data }) => {

            resolve( data );

        }).catch( err => {

            reject( err );

        });

    });

};

export const createOrder = (
    planData: Array<Record<string, any>>,
    manualRefundInfo: IManualRefundInfo | _.IEmptyObject,
    amount: string,
    currency: string,
    shippingInfo: IBillingInfo|undefined,
    country?: string,
    promotionType?: string,
    couponCode?: string
): Promise<any> => {

    const items = planData;

    return new Promise(( resolve, reject ) => {

        const data: Record<string, any> = ObjectMethods.deepClone({
            items,
            amount,
            currency,
            context: { country, timezone: getIANATimeZone(), source: getQuerySource() },
            promotionType,
            couponCode: couponCode ? [couponCode] : undefined
        });

        if ( !isEmptyObject( manualRefundInfo )) {

            data.manualRefundInfo = manualRefundInfo;

        }

        if ( typeof shippingInfo !== 'undefined' ) {

            data.shippingInfo = shippingInfo;

        }
        http.post( `/shop/orders/`, data ).then( res => {

            resolve( res.data );

        }).catch( err => {

            reject( err );

        });

    });

};

/**
 * 用户切换、购买套餐统一处理
 */
export const subscriptions = async (

    planData: Array<Record<string, any>>,
    needPay: boolean,
    mode: ESubscribeMode,
    manualRefundInfo: IManualRefundInfo,
    shippingAddress: IBillingInfo,
    currency: string,
    amount: string,
    country: string,
    router: VueRouter,
    freeOrderArgs?: IPreviewFreeOffer_BodyArgs,
    type?: string,
    couponCode?: string

): Promise<boolean> => {

    /**
     * 创建订阅
     */
    if ( needPay === undefined ) {

        try {

            const preview: any = await previewSubscriptions( planData );
            needPay = preview.payment;

        } catch ( err ) {

            console.error( err );

        }

    }

    const sub = await ( freeOrderArgs ? createFreeOrder({ ...freeOrderArgs, source: getQuerySource() })
        : createOrder( planData, manualRefundInfo,  amount, currency, needPay ? shippingAddress : void 0, country, type, couponCode ));

    let redirectUrl: string;
    const query: Record<string, any> = {};
    /**
     * 如果需要付费
     *
     * 支付完成后一律跳转到order complete页面
     */
    if ( needPay ) {

        router.push({

            path: '/checkout/pay/',
            query: {
                o_id: sub.id,
                mode,
                platform: config.platform
            }

        })?.catch( console.error );
        return true;

    } else {

        redirectUrl = `/user/complete/`;
        query.mode = mode;
        query.id = sub.id;// orderId

    }
    router.push({

        path: redirectUrl,
        query

    })?.catch( console.error );
    return true;

};

export const getPaymentHistory = ( year: number|string, page: number, type: string|null|undefined, limit = 20 ): Promise<ICommonRes<IOrder>> => {

    let start;
    let end;
    year = year.toString();
    if ( year !== 'all' ) {

        start = dayjs( year, 'YYYY' ).unix() * 1000;
        end = dayjs( year, 'YYYY' ).add( 1, 'y' ).unix() * 1000;

    }
    return new Promise(( resolve, reject ) => {

        http.get( `/shop/orders/?page=${page}&limit=${limit}${year !== 'all' ? `&not_before=${start}&not_after=${end}` : ''}${type ? `&product_type=${type}` : ''}` ).then( res => {

            resolve( res.data );

        }).catch( err => {

            reject( err );

        });

    });

};

export const getOrders = ( page: number, limit = 20, notAfter = 0, notBefore = 0 ): any => {

    return new Promise(( resolve, reject ) => {

        http.get( `/shop/orders?page=${page}&limit=${limit}&not_before=${notBefore}&not_after=${notAfter}` ).then( res => {

            resolve( res.data );

        }).catch( err => {

            reject( err );

        });

    });

};

// export const calculateTax = ( data ): any => {

//     return new Promise(( resolve, reject ) => {

//         http.post( '/taxes/', data, {
//             baseURL: config.baseUrl + 'v1.0/'
//         }).then( res => {

//             resolve( res.data );

//         }).catch( err => {

//             reject( err );

//         });

//     });

// };

export const getCloudProductListsAPI = async ( options: IGetCloudProductsArgs ): Promise<IGetCloudProductsResponse> => {

    const { data } = await http.post<IGetCloudProductsResponse>( '/v2/cloud/products', options, { baseURL: config.baseUrl });

    return data;

};

/**
 * 传入国家二字简码构造国家信息，包括二字简码code和国家名称name以及国旗图片地址
 * @param allCountryList 接口获取的所有国家的二字简码code和name
 * @param countriesList plan中包含的可用国家
 * @param data 整个数据
 */
export const getSupportedCountries = ( allCountryList: ICountryItem[], countriesList: string[], data: IProductSeries | IStorageLoc ): any => {

    data.supportedCountryList = countriesManager.getCountryInfoList( countriesList );

};

export const getCloudProductLists =
async (
    options: IGetCloudProductsArgs,
    /** 用户国家，用于筛选默认套餐，默认为'all' */
    userCountry: string = 'all',
    legacyPage?: string
): Promise<{
    cloudProductLists: ICloudProducts;
    storageLocList: any;
    country: string;
}> => {

    try {

        nprogress.set( 0.6 );
        let res = await getCloudProductListsAPI({ ...options, lang: options.lang ?? 'en' });

        /** 若后端返回系列与套餐都为空，请求最新的套餐 */
        if ( options.legacyTag && !res.plans && !res.planSeries ) {

            res = await getCloudProductListsAPI({ ...options, legacyTag: '', lang: options.lang ?? 'en' });

        }

        const cloudProductLists: ICloudProducts = { ...( res as ICloudProducts ), defaultStorageRegion: {} as ICloudProducts['defaultStorageRegion'], cloudModels: [] };

        let planSeries: Record<string, IProductSeries> = {};
        const plans: Record<string, IProductPlan> = {};
        const storageLocList: Record<string, IStorageLoc> = {};

        Object.keys( cloudProductLists.planSeries ).map( key => {

            planSeries[key] = {
                ...cloudProductLists.planSeries[key],
                plans: [] as [],
                supportedCountryList: [] as ISupportCountry[]
            };

        });
        // 将套餐放到系列下，将系列的style赋予每个套餐让已有组件不需更改逻辑
        Object.keys( cloudProductLists.plans ).map(( id ) => {

            plans[id] = { ...cloudProductLists.plans[id], title: '' };
            plans[id].style['plan.display_color'] = planSeries[plans[id].seriesKey].style['series.display_color'];
            plans[id].style['plan.recommended'] = planSeries[plans[id].seriesKey].style['series.recommended'];
            plans[id].title = cloudProductLists.planSeries[plans[id].seriesKey].i18n.name;
            planSeries[plans[id].seriesKey].plans.push( plans[id] );

        });

        // 获取机型的详细信息，包括图片，链接，名称
        Object.keys( cloudProductLists.deviceModelGroups ).map( group => {

            const models = cloudProductLists.deviceModelGroups[group].models;
            const modelArr: IDeviceModelGroup['modelsDetails'] = [];
            for ( const model of models ) {

                if ( !modelArr.find( item => item.key === model )) {

                    modelArr.push({
                        image: `${cloudProductLists.deviceModels[model].styles.imageUrl ?? ''}`,
                        link: `${cloudProductLists.deviceModels[model].styles.link ?? config.reolink}`,
                        text: cloudProductLists.deviceModels[model]?.title ?? '',
                        visible: cloudProductLists.deviceModels[model]?.visible ?? 'disabled',
                        key: model
                    });

                }

            }

            cloudProductLists.deviceModelGroups[group].modelsDetails = modelArr;

        });

        /** 没传legacyTag且位于以下页面时，不显示legacy套餐 */
        if ( !options?.legacyTag &&
            ( legacyPage === 'update-plan' ||
              legacyPage === 'subscribe' ||
              legacyPage === 'homepage' )) {

            // 过滤legacy plan
            const newPlanSeries = {};
            Object.keys( planSeries ).map( series => {

                if ( !planSeries[series].legacyTag ) {

                    newPlanSeries[series] = planSeries[series];

                }

            });
            planSeries = newPlanSeries;

        }

        // 处理每个系列的可用国家
        Object.keys( planSeries ).map( series => {

            const countryGroupNames = planSeries[series].countryGroups;
            let planSeriesCountries: string[] = [];
            for ( const countryGroupName of countryGroupNames ) {

                const countries = cloudProductLists.countryGroups[countryGroupName]?.countries ?? [];

                for ( const country of countries ) {

                    planSeriesCountries = planSeriesCountries.concat( country.key );

                }

                planSeries[series].supportedCountryList = countriesManager.getCountryInfoList( planSeriesCountries );

            }

        });

        /**
         * 搜索的国家
         *  - 登录时：用户的国家
         *  - 未登录：接口返回的国家（IP）
         */
        let searchCountry: string;
        if ( userCountry && userCountry !== 'all' ) {

            searchCountry = userCountry;

        }
        else {

            searchCountry = cloudProductLists.country;

        }

        // 将套餐系列按存储地进行分类
        Object.keys( cloudProductLists.storageRegions ).map(( region ) => {

            const deviceGroupsListAll = {} as IStorageLoc['deviceGroupsList'];
            const _region = {} as IStorageLoc;

            Object.keys( planSeries ).map(( key ) => {

                deviceGroupsListAll[planSeries[key].deviceGroup] = deviceGroupsListAll[planSeries[key].deviceGroup] ?? {};
                deviceGroupsListAll[planSeries[key].deviceGroup].planList = deviceGroupsListAll[planSeries[key].deviceGroup].planList ?? [];
                if ( planSeries[key].storageRegion === region ) {

                    if ( key.includes( 'basic' )) {

                        deviceGroupsListAll[planSeries[key].deviceGroup].basicSerial = planSeries[key];

                    } else {

                        deviceGroupsListAll[planSeries[key].deviceGroup].planList.push({ products: planSeries[key].plans.sort(( a, b ) => a.style['plan.sort'] - b.style['plan.sort'] ), serialName: key, sortOrder: planSeries[key].style['series.sort'] });

                    }

                }

                _region['planSeries'] = _region['planSeries'] ?? {};
                if ( planSeries[key].storageRegion === region ) {

                    _region['planSeries'][key] = planSeries[key];

                }

            });
            if ( _region['planSeries'] && Object.keys( _region['planSeries'] ).length > 0 ) {

                storageLocList[region] = _region;
                storageLocList[region].deviceGroupsList = deviceGroupsListAll;

            }

        });
        // 获取每个存储地的可用国家,区分基础套餐与高级套餐
        // 获取每个存储地的deviceGroup
        let defaultStorageRegion: ICloudProducts['defaultStorageRegion'] | null = null;
        const cloudModels: string[] = [];
        Object.keys( storageLocList ).map(( loc ) => {

            /** 当前存储地所对应的国家组的key列表 */
            const countriesGroupsList: string[] = [];
            /** 当前存储地所对应国家组的国家key列表 */
            const countriesList: string[] = [];
            const deviceGroupsIdList: number[] = [];
            const storageDeviceGroupsList = storageLocList[loc].deviceGroupsList;

            Object.keys( storageLocList[loc]['planSeries'] ).map(( key ) => {

                // 对国家信息进行处理
                const countryGroupName = storageLocList[loc]['planSeries'][key].countryGroups;

                for ( const groupKey of countryGroupName ) {

                    if ( !countriesGroupsList.includes( groupKey )) {

                        countriesGroupsList.push( groupKey );

                    }

                }

                // 对机型组信息进行处理
                const deviceGroupName = storageLocList[loc]['planSeries'][key].deviceGroup;

                if ( !deviceGroupsIdList.includes( cloudProductLists.deviceModelGroups[deviceGroupName].id )) {

                    deviceGroupsIdList.push( cloudProductLists.deviceModelGroups[deviceGroupName].id );

                    storageDeviceGroupsList[deviceGroupName].id = cloudProductLists.deviceModelGroups[deviceGroupName].id;
                    storageDeviceGroupsList[deviceGroupName].title = cloudProductLists.deviceModelGroups[deviceGroupName].i18n.name;
                    storageDeviceGroupsList[deviceGroupName].des = cloudProductLists.deviceModelGroups[deviceGroupName].i18n.description;
                    storageDeviceGroupsList[deviceGroupName].models = cloudProductLists.deviceModelGroups[deviceGroupName].models;
                    storageDeviceGroupsList[deviceGroupName].modelsDetails = cloudProductLists.deviceModelGroups[deviceGroupName].modelsDetails;

                    const models = cloudProductLists.deviceModelGroups[deviceGroupName].modelsDetails.map( item => item.text );
                    models.map( model => {

                        if ( !cloudModels.includes( model )) {

                            cloudModels.push( model );

                        }

                    });

                }

            });

            for ( const countryGroup of countriesGroupsList ) {

                /** 国家组所包含的国家信息列表 */
                const countries = cloudProductLists.countryGroups[countryGroup]?.countries ?? [];

                for ( const country of countries ) {

                    if ( !countriesList.includes( country.key )) {

                        countriesList.push( country.key );

                    }

                }

            }

            storageLocList[loc].supportedCountryList = countriesManager.getCountryInfoList( countriesList );

            if ( countriesList.includes( searchCountry )) {

                defaultStorageRegion = !defaultStorageRegion ? {
                    i18n: cloudProductLists.storageRegions[loc].i18n,
                    key: cloudProductLists.storageRegions[loc].key,
                    loc: loc,
                    sortOrder: cloudProductLists.storageRegions[loc].sortOrder
                } : defaultStorageRegion;

                if ( cloudProductLists.storageRegions[loc].sortOrder < defaultStorageRegion.sortOrder ) {

                    defaultStorageRegion = {
                        i18n: cloudProductLists.storageRegions[loc].i18n,
                        key: cloudProductLists.storageRegions[loc].key,
                        loc: loc,
                        sortOrder: cloudProductLists.storageRegions[loc].sortOrder
                    };

                }

            }

        });
        cloudProductLists.defaultStorageRegion = defaultStorageRegion;
        cloudProductLists.cloudModels = cloudModels;
        cloudProductLists.planSeries = planSeries;
        cloudProductLists.plans = plans;
        // 处理流量机型 snapeak暂无
        // const supportedTrafficDeviceList: any[] = [];
        // const modelTrafficUniArr: any[] = [];

        // Object.keys( cloudProductLists.deviceModels ).map(( model ) => {

        //     if ( cloudProductLists.deviceModels[model].enableRelay && !modelTrafficUniArr.includes( cloudProductLists.deviceModels[model].title )) {

        //         modelTrafficUniArr.push( cloudProductLists.deviceModels[model].title );
        //         supportedTrafficDeviceList.push({
        //             image: `${typeof cloudProductLists.deviceModels[model].styles.imageUrl !== 'undefined' && cloudProductLists.deviceModels[model].styles.imageUrl !== '' ? cloudProductLists.deviceModels[model].styles.imageUrl : ''}`,
        //             link: `${typeof cloudProductLists.deviceModels[model].styles.link !== 'undefined' && cloudProductLists.deviceModels[model].styles.link !== '' ? cloudProductLists.deviceModels[model].styles.link : config.reolink}`,
        //             text: cloudProductLists.deviceModels[model].title,
        //             visible: cloudProductLists.deviceModels[model].visible,
        //             key: cloudProductLists.deviceModels[model].key,
        //             enableRelay: cloudProductLists.deviceModels[model].enableRelay
        //         });

        //     }

        // });
        return { cloudProductLists, storageLocList: storageLocList, country: cloudProductLists.country };

    } catch ( err ) {

        unfoldError( 'errors.server_error', 99999 );
        nprogress.done();
        console.log( err );
        return { cloudProductLists: {} as ICloudProducts, storageLocList: {}, country: '' };

    }

};

export const isVATenabled = ( country: string ): Promise<boolean> => {

    return new Promise(( resolve, reject ) => {

        http.post( `/shop/taxes/settings/`, { country }).then( res => {

            resolve( res.data.vatEnabled );

        }).catch( err => {

            reject( err );

        });

    });

};

export const isVATValid = ( number: string ): Promise<{ valid: boolean; address: string; company: string; }> => {

    return new Promise(( resolve, reject ) => {

        http.post( `/shop/taxes/vat/numbers`, { vatNumber: number }).then( res => {

            resolve( res.data );

        }).catch( err => {

            reject( err );

        });

    });

};

export const getPlanById = async ( id: string, lang = 'en' ): Promise<IGetPlanByIdRes> => {

    const { data } = await http.get<IGetPlanByIdRes>( `/cloud/plans/${id}?lang=${lang}` );

    return data;

};

export const getTrafficProductsAPI = async ( lang = 'en' ): Promise<any> => {

    return new Promise(( resolve, reject ) => {

        http.get( `/cloud/traffic/plans?lang=${lang}` ).then( res => {

            resolve( res.data );

        }).catch( error => {

            reject( error );

        });

    });

};

export const getTrafficById = async ( id: string, lang = 'en' ): Promise<any> => {

    return new Promise(( resolve, reject ) => {

        http.get( `/cloud/traffic/plans/${id}?lang=${lang}` ).then( res => {

            resolve( res.data );

        }).catch( error => {

            reject( error );

        });

    });

};

export interface IShippingAddress {
    country: string;
    state: string;
    city: string;
    firstName: string;
    lastName: string;
    postalCode: string;
    line1: string;
    line2: string;
    company: string;
}

export const getUserAddresses = async ( args?: IGetUserAddressesQueryArgs ): Promise<IUserAddresses> => {

    const res = ( await stmHttp.get<IUserAddresses>( `/shop/user/addresses${objectToQueryString( args )}` ));

    return res.data;

};

export const getReolinkAddresses = async (): Promise<IReolinkAddresses> => {

    const res = ( await stmHttp.get<IReolinkAddresses>( '/shop/user/reo-addresses' ));

    return res.data;

};

export const createUserAddress = async ( args: TCreateUserAddressArg ): Promise<void> => {

    await http.post( '/shop/user/addresses', args );

};

export const updateUserAddress = async( args: TUpdateUserAddressArg ): Promise<void> => {

    await http.put( '/shop/user/addresses', args );

};

/**
 * 根据planId获取plan，调用前请先保证productManager初始化
 * @param planId
 */
export const getIPlan = async ( planId: string, lang?: string ): Promise<IPlan> => {

    const plan = await productsManager.getPlanById( planId );
    const series = productsManager.getPlanSeriesByKey( plan.seriesKey );

    const productSupported = productsManager.getDeviceModelGroupByKey( series?.deviceGroup ?? '' )?.modelsDetails
        ?.filter( item => item.visible === 'enabled' );
    let productSupportedString = '';
    if ( !productSupported?.length ) {

        productSupportedString = '';

    } else {

        let texts = productSupported.map( item => item.text );
        texts = Array.from( new Set( texts ));
        if ( !texts?.length ) {

            productSupportedString = '';

        } else {

            productSupportedString = listToString( texts, lang );

        }

    }

    plan.storageRegion = productsManager.getStorageRegionByKey( series?.storageRegion ?? '' )?.i18n?.name;
    plan.productSupportedString = productSupportedString;
    return plan;

};
